/**
 * The run token: one string that holds everything the restore agent needs, for an engineer to
 * paste into `vsrecovery run --token-stdin` on a server that has no daemon.
 *
 * The format is the restore agent's (VSAgentRestore, tag vsrecovery-integration-6,
 * docs/DRIVER-CONTRACT.md section 15, and tools/run_token.js, which this follows line for line):
 *
 *     VSR1.<base64url, no padding, of the UTF-8 JSON>.<first 16 hex of SHA-256("VSR1.<payload>")>
 *
 * It is made HERE, in the browser, and goes nowhere else: a version 1 token is not encrypted,
 * and one for a real restore carries credentials. It is never sent to Orchestrate's back end,
 * never stored and never put in an address. Only the envelope's own members are allowed - the
 * agent refuses an unknown one by name.
 */
export interface RunTokenEnvelope {
    contract: 1
    command: string
    runId: string
    options: Record<string, string | number | boolean>
    issuedFor: string
    issuedAt: string
    expiresAt: string
}

async function check16(text: string): Promise<string> {
    const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text)))
    return Array.from(digest.slice(0, 8), (b) => b.toString(16).padStart(2, '0')).join('')
}

export async function makeRunToken(envelope: RunTokenEnvelope | string): Promise<string> {
    const json = typeof envelope === 'string' ? envelope : JSON.stringify(envelope)
    let binary = ''
    for (const b of new TextEncoder().encode(json)) binary += String.fromCharCode(b)
    const text = `VSR1.${btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`
    return `${text}.${await check16(text)}`
}

const stamp = (date: Date) => `${date.toISOString().slice(0, 19)}Z`
/** How long a token made here can be used. The target's clock decides, with no allowance. */
export const TOKEN_MINUTES = 30

/**
 * `issuedFor`, which the agent shows to the engineer word for word before the run and never
 * reads. Always the same three parts (the restore agent session, 13 Oct 2026):
 *     <engineer> | <recovery request> | <object>
 */
export const issuedFor = (engineer: string | null | undefined, recoveryRequest: string, object: string) => [engineer || 'unknown engineer', recoveryRequest, object].join(' | ').slice(0, 400)

/** The self-test, to be run by hand. It restores nothing and needs no secret. */
export function selfTestEnvelope(runId: string, engineer: string | null | undefined, now = new Date()): RunTokenEnvelope {
    return {
        contract: 1,
        command: 'self-test',
        runId,
        options: { '--seconds': 20, '--steps': 5 },
        // No recovery request and no object: the self-test restores nothing.
        issuedFor: issuedFor(engineer, 'self-test', 'no object'),
        issuedAt: stamp(now),
        expiresAt: stamp(new Date(now.getTime() + TOKEN_MINUTES * 60 * 1000)),
    }
}
